import { useDispatch } from 'react-redux';
import {
    PROGRESS_PROCESSES,
    ProgressConfigT,
    setProgressProcess,
    setProgressStatus,
    updateProgressConfig,
} from '@/store/slices/progressSlice';
import { store } from '@/store/store';
import { GuidT, PromiseResultIF } from '@/types/common';
import { aiApi } from '@/api/aiApi/aiApi';
import { AI_TASK_TYPES, AiTaskModelIF, AiTaskTypeT } from '@/api/aiApi/types';
import { filesApi } from '@/api/filesApi/filesApi';
import { propertyApi } from '@/api/propertyApi/propertyApi';
import { TASK_LABEL } from '@/hooks/uploadingFiles/uploadingFiles.config';
import { getFormattedResult } from '@/hooks/uploadingFiles/uploadingFiles.helpers';
import { UseFileProgressIF } from '@/hooks/uploadingFiles/uploadingFiles.types';
import { getDefaultSeasons } from '@/helpers/normaliseProperties';
import { DELAY_TIME_1000, pollingAiTask } from '@/helpers/pollingAiTask';
import { getLocalStorageItem, setLocalStorageItem } from '@/helpers/storage';
import { delay } from '@/helpers/utils';
import { ClothingFormIF } from '@/components/forms/EditClothingForm/EditClothingForm.types';
import { TASK_STATUSES } from '@/constants/statuses';
import { STORAGE_KEYS } from '@/constants/storageKeys';

export const useUploadingFiles = (
    config?: Partial<ProgressConfigT>,
): UseFileProgressIF => {
    const dispatch = useDispatch();

    const [addFile] = filesApi.useAddFileMutation();
    const [createAiTask] = aiApi.useCreateAiTaskMutation();
    const [getAiTask] = aiApi.useGetAiTaskMutation();

    const uploadToServer = async (files: File[]): Promise<GuidT[] | null> => {
        dispatch(setProgressProcess(PROGRESS_PROCESSES.Upload));
        dispatch(setProgressStatus(TASK_STATUSES.Uploading));

        const uploadPromises = files.map(async (file, index) => {
            await delay(index * DELAY_TIME_1000);
            const formData = new FormData();
            formData.append('File', file);
            return addFile(formData).unwrap();
        });

        const uploadsResult: PromiseResultIF<GuidT> =
            await Promise.allSettled(uploadPromises);

        return uploadsResult
            .map((result) =>
                result.status === 'fulfilled' ? result.value : null,
            )
            .filter((guid) => guid !== null);
    };

    const getFinishedAiTasks = async (
        tasksData: AiTaskModelIF[],
        taskType: AiTaskTypeT,
    ): Promise<AiTaskModelIF[]> => {
        dispatch(
            updateProgressConfig({
                Processing: {
                    label: TASK_LABEL[taskType],
                },
            }),
        );
        dispatch(setProgressStatus(TASK_STATUSES.Processing));

        const tasksFinishedResult: PromiseResultIF<AiTaskModelIF | null> =
            await Promise.allSettled(
                tasksData.map((taskData) =>
                    pollingAiTask(taskData.guid, (guid) =>
                        getAiTask(guid).unwrap(),
                    ),
                ),
            );

        return tasksFinishedResult
            .map((result) =>
                result.status === 'fulfilled' && result.value
                    ? result.value
                    : null,
            )
            .filter((data) => data !== null);
    };

    const startAiTask = async (
        formattedData: ClothingFormIF[],
        taskType: AiTaskTypeT,
    ): Promise<AiTaskModelIF[] | null> => {
        dispatch(setProgressStatus(TASK_STATUSES.Pending));

        const createAiTaskPromises = formattedData.map(
            async ({ imageId }, index) => {
                await delay(index * DELAY_TIME_1000);
                return createAiTask({
                    fileGuid: imageId,
                    taskType,
                }).unwrap();
            },
        );

        const createAiTaskResult: PromiseResultIF<AiTaskModelIF> =
            await Promise.allSettled(createAiTaskPromises);

        return createAiTaskResult
            .map((result) =>
                result.status === 'fulfilled' &&
                result.value?.status === TASK_STATUSES.Pending
                    ? result.value
                    : null,
            )
            .filter((guid) => guid !== null);
    };

    const createAiTasks = async (
        imagesId: GuidT[],
        aiTasks: AiTaskTypeT[],
    ): Promise<ClothingFormIF[] | null> => {
        let formattedData = imagesId.map((imageId) =>
            getFormattedResult({ imageId }),
        );

        for (const aiTask of aiTasks) {
            const startedTasks = await startAiTask(formattedData, aiTask);

            if (!startedTasks) {
                continue;
            }

            const finishedTasks = await getFinishedAiTasks(
                startedTasks,
                aiTask,
            );

            if (!finishedTasks) {
                continue;
            }

            const finishedMap = new Map(
                finishedTasks.map((item) => [item.inputFileGuid, item]),
            );

            if (aiTask === AI_TASK_TYPES.RemoveBg) {
                formattedData = formattedData.map((formattedItem) => {
                    const finishedItem = finishedMap.get(
                        formattedItem.imageId || '',
                    );
                    return {
                        ...formattedItem,
                        imageId:
                            finishedItem?.outputFileGuid ||
                            formattedItem.imageId,
                    };
                });
            }

            if (aiTask === AI_TASK_TYPES.ClothingAttributes) {
                const categories =
                    propertyApi.endpoints.getCategoriesSelectProperties.select()(
                        store.getState(),
                    ).data;

                formattedData = formattedData.map((formattedItem) => {
                    const finishedItem = finishedMap.get(
                        formattedItem.imageId || '',
                    );
                    const outputData = finishedItem?.outputData || null;
                    const categoryGuid = outputData?.category?.guid || '';

                    return {
                        ...formattedItem,
                        categoryGuid,
                        colorGuid: outputData?.color?.guid || '',
                        materialId: outputData?.material?.guid || null,
                        fit: outputData?.style,
                        suitableSeason: getDefaultSeasons(
                            categories,
                            categoryGuid,
                        ),
                    };
                });
            }
        }

        return formattedData;
    };

    const finishUploading = async (
        processedData: ClothingFormIF[] | null,
    ): Promise<GuidT[] | null> => {
        if (!processedData?.length) {
            dispatch(setProgressStatus(TASK_STATUSES.Failed));
            return null;
        }

        const existingData =
            getLocalStorageItem<ClothingFormIF[]>(STORAGE_KEYS.ClothingForm) ||
            [];

        setLocalStorageItem<ClothingFormIF[]>(STORAGE_KEYS.ClothingForm, [
            ...processedData,
            ...existingData,
        ]);

        dispatch(setProgressStatus(TASK_STATUSES.Completed));

        return processedData
            .map((data) => data.guid || null)
            .filter((guid) => guid !== null);
    };

    const uploadFiles = async (
        files: File[],
        aiTasks?: AiTaskTypeT[],
    ): Promise<GuidT[] | null> => {
        if (config) {
            dispatch(updateProgressConfig(config));
        }

        const fileGuids = await uploadToServer(files);

        if (!fileGuids?.length) {
            dispatch(setProgressStatus(TASK_STATUSES.FailedAll));

            return null;
        }

        dispatch(
            updateProgressConfig({
                [TASK_STATUSES.Completed]: {
                    label: `Загружено ${fileGuids?.length} из ${files?.length}`,
                },
            }),
        );

        if (aiTasks?.length) {
            const processedFileResult = await createAiTasks(fileGuids, aiTasks);

            return finishUploading(processedFileResult);
        }

        dispatch(setProgressStatus(TASK_STATUSES.Completed));

        return fileGuids;
    };

    return {
        uploadFiles,
    };
};
